import {Checkbox} from "primereact/checkbox";
import {InputTextarea} from "primereact/inputtextarea";
import {SelectButton} from "primereact/selectbutton";
import {useTranslation} from "react-i18next";

import {COMMISSION_ORDER_TYPES, type CommissionOrderType} from "@reva/shared/value-order";

import {selectPermissionCheck} from "../../redux/authSelectors";
import {useRevaSelector} from "../../redux/revaHooks";
import {PERMISSION_RULE_CADASTRE} from "../../util/permissions";
import {
    type AllowedCommissionOrderOptions,
    formatOrderTypeList,
    orderTypeLabelKey,
} from "../../util/valueOrderEntitlements";
import {
    type CommissionOrderForm,
    DOCUMENT_OPTIONS,
    type FormChange,
    INTERIOR_OPTIONS,
    splitByAllowed,
} from "./commissionOrderUtil";

/**
 * Hinweis auf Optionen, die der Dialog nicht anbietet, weil sie beim Auftraggeber nicht freigeschaltet sind.
 * @param id DOM-ID
 * @param labels Anzeigenamen der fehlenden Optionen
 */
function OptionsNotEnabledHint({id, labels}: {id: string; labels: string[]}) {
    const {t} = useTranslation();
    if (labels.length === 0) return null;
    return (
        <p id={id} className="text-sm text-muted-color mt-2 mb-0">
            {t("commissionOrderDialog.optionsNotEnabled", {count: labels.length, list: labels.join(", ")})}
        </p>
    );
}

interface DocumentOptionsFieldProps {
    isDocumentCollection: boolean;
    documentOptions: string[];
    allowedOptions: string[] | undefined;
    onToggleOption: (option: string) => void;
    disabled: boolean;
    showInvalid: boolean;
}

function DocumentOptionsField({
    isDocumentCollection,
    documentOptions,
    allowedOptions,
    onToggleOption,
    disabled,
    showInvalid,
}: DocumentOptionsFieldProps) {
    const {t} = useTranslation();
    const hasCadastrePermission = useRevaSelector(selectPermissionCheck(PERMISSION_RULE_CADASTRE));
    if (!isDocumentCollection) return null;
    const optionsInvalid = showInvalid && documentOptions.length === 0;
    const {offered, notEnabled} = splitByAllowed(
        DOCUMENT_OPTIONS.filter((o) => o !== "nonOfficialCadastralMap" || !hasCadastrePermission),
        allowedOptions,
    );
    return (
        <div className="field mb-0 mt-4">
            <label id="commission-document-options-label" className={optionsInvalid ? "text-red-500" : undefined}>
                {t("commissionOrderDialog.documents.title")}
                <span className="commission-required">*</span>
            </label>
            <div
                className="formgrid commission-grid mt-1"
                role="group"
                aria-labelledby="commission-document-options-label"
            >
                {offered.map((option) => (
                    <div key={option} className="col-span-6 flex items-center gap-2 mb-2">
                        <Checkbox
                            inputId={`doc-${option}`}
                            checked={documentOptions.includes(option)}
                            onChange={() => onToggleOption(option)}
                            disabled={disabled}
                        />
                        <label htmlFor={`doc-${option}`} className="cursor-pointer">
                            {t(`commissionOrderDialog.documents.option.${option}`)}
                        </label>
                    </div>
                ))}
            </div>
            <OptionsNotEnabledHint
                id="commission-document-options-not-enabled"
                labels={notEnabled.map((o) => t(`commissionOrderDialog.documents.option.${o}`))}
            />
            <p className="mt-2 mb-0">{t("commissionOrderDialog.documents.hint")}</p>
        </div>
    );
}

interface InteriorInspectionPanelProps {
    orderType: CommissionOrderType | null;
    notes: string | null;
    onNotesChange: (notes: string) => void;
    interiorOptions: string[];
    allowedOptions: string[] | undefined;
    onToggleOption: (option: string) => void;
    disabled: boolean;
}

function InteriorInspectionPanel({
    orderType,
    notes,
    onNotesChange,
    interiorOptions,
    allowedOptions,
    onToggleOption,
    disabled,
}: InteriorInspectionPanelProps) {
    const {t} = useTranslation();
    if (orderType !== "InteriorInspection") return null;
    const {offered, notEnabled} = splitByAllowed(INTERIOR_OPTIONS, allowedOptions);
    return (
        <>
            <div className="field mb-0 mt-4">
                <label htmlFor="commission-notes">{t("commissionOrderDialog.notes")}</label>
                <InputTextarea
                    id="commission-notes"
                    className="w-full"
                    rows={4}
                    autoResize
                    value={notes ?? ""}
                    onChange={(e) => onNotesChange(e.target.value)}
                    disabled={disabled}
                />
            </div>
            <div className="field mt-4">
                <label id="commission-interior-options-label">
                    {t("commissionOrderDialog.inspection.optionsTitle")}
                </label>
                <div className="flex gap-6" role="group" aria-labelledby="commission-interior-options-label">
                    {offered.map((option) => (
                        <div key={option} className="flex items-center gap-2">
                            <Checkbox
                                inputId={`interior-${option}`}
                                checked={interiorOptions.includes(option)}
                                onChange={() => onToggleOption(option)}
                                disabled={disabled}
                            />
                            <label htmlFor={`interior-${option}`} className="cursor-pointer">
                                {t(`commissionOrderDialog.inspection.option.${option}`)}
                            </label>
                        </div>
                    ))}
                </div>
                <OptionsNotEnabledHint
                    id="commission-interior-options-not-enabled"
                    labels={notEnabled.map((o) => t(`commissionOrderDialog.inspection.option.${o}`))}
                />
            </div>
        </>
    );
}

const toggled = (options: string[], option: string) =>
    options.includes(option) ? options.filter((o) => o !== option) : [...options, option];

interface OrderTypeSectionProps {
    form: Pick<CommissionOrderForm, "orderType" | "notes" | "interiorOptions" | "documentOptions">;
    onChange: (changes: FormChange) => void;
    allowedOrderTypes: CommissionOrderType[] | null;
    missingOrderTypes: CommissionOrderType[] | undefined;
    allowedOrderOptions: AllowedCommissionOrderOptions | null;
    disabled: boolean;
    showInvalid: boolean;
}

export function OrderTypeSection({
    form: {orderType, notes, interiorOptions, documentOptions},
    onChange,
    allowedOrderTypes,
    missingOrderTypes,
    allowedOrderOptions,
    disabled,
    showInvalid,
}: OrderTypeSectionProps) {
    const {t} = useTranslation();
    const orderTypeOptions = COMMISSION_ORDER_TYPES.filter(
        (o) => !allowedOrderTypes || allowedOrderTypes.includes(o),
    ).map((o) => ({label: t(orderTypeLabelKey(o)), value: o}));
    return (
        <div className="commission-panel">
            <div className="field mb-0">
                <label id="commission-order-type-label">
                    {t("commissionOrderDialog.orderType")}
                    <span className="commission-required">*</span>
                </label>
                <div>
                    <SelectButton
                        id="commission-order-type"
                        aria-labelledby="commission-order-type-label"
                        value={orderType}
                        options={orderTypeOptions}
                        disabled={disabled}
                        invalid={showInvalid && !orderType}
                        onChange={(e) => onChange({orderType: e.value, interiorOptions: [], documentOptions: []})}
                    />
                </div>
                {missingOrderTypes && missingOrderTypes.length > 0 && (
                    <p id="commission-order-types-not-enabled" className="text-sm text-muted-color mt-2 mb-0">
                        {t("commissionOrderDialog.orderTypesNotEnabled", {
                            list: formatOrderTypeList(t, missingOrderTypes),
                        })}
                    </p>
                )}
            </div>
            <InteriorInspectionPanel
                orderType={orderType}
                notes={notes}
                onNotesChange={(value) => onChange({notes: value})}
                interiorOptions={interiorOptions}
                allowedOptions={allowedOrderOptions?.InteriorInspection}
                onToggleOption={(option) => onChange((f) => ({interiorOptions: toggled(f.interiorOptions, option)}))}
                disabled={disabled}
            />
            <DocumentOptionsField
                isDocumentCollection={orderType === "DocumentCollection"}
                documentOptions={documentOptions}
                allowedOptions={allowedOrderOptions?.DocumentCollection}
                onToggleOption={(option) => onChange((f) => ({documentOptions: toggled(f.documentOptions, option)}))}
                disabled={disabled}
                showInvalid={showInvalid}
            />
        </div>
    );
}
